// janus-engine/reactions/page-trigger-readers.ts — lane RX (2026-09-28): the TriggerReaders a BROWSER consumer hands the dispatch
// decision (trigger-dispatch.ts), built from the rows the page holds. Every reader answers from held state or throws
// TriggerInputUnavailable; none guesses. The PHP row rules live in php-row-rules.ts (shared with the JanusCtx adapter). Geometry is
// the front-end reach rule (ft3d-combat-distance-v3 rim; PHP passes target_rim: true to match — owner 2026-09-28).
import { combatCenter, combatSizeFt } from '../actions/ability-preconditions.js';
import { movementReachDistanceFt, swarmMovementBody } from '../movement/body-geometry.js';
import { getSwarmTemplateForCreature } from '../../shared/swarm-templates.js';
import { TriggerInputUnavailable, type Row, type TriggerReaders } from './trigger-dispatch.js';
import { frontEndPl, fullDefinition, heldTriggerCharges, inActiveCombat, naturalWeaponCount, opposingTeams, posCoord, storedStatusItemIds,
  triggerRangePasses } from './php-row-rules.js';

const record = (v: unknown): Row | null => v !== null && typeof v === 'object' && !Array.isArray(v) ? v as Row : null;
const upper = (v: unknown): string => String(v ?? '').trim().toUpperCase();
const scalar = (v: unknown): unknown => { let x = v; while (Array.isArray(x)) x = x[0]; return x; };
/** A compiled member row ELIDES a zero numeric column only when it carries both identity anchors (build_encounter_creature_arr.php). */
const EC_LEAN_ZERO = new Set(['reaction', 'mana', 'aps', 'luck', 'v_current', 'v_temp', 'movement_pct', 'encounter_creature_dead']);

export interface PageTriggerHost {
  encounter: Row;
  /** Catalog definition for an id from the row / encounter catalog or the object cache (shells allowed — fullDefinition() filters). */
  definition(id: string): Row | null;
  /** layer_entries / layer_value / item_field twins (the item-arr-readers globals). */
  entries(row: Row | null, key: string): unknown[];
  value(row: Row | null, key: string): unknown;
  itemField(row: Row | null, key: string): unknown;
}

/** calculate_range_from_caster_to_target(..., target_rim: true) — REACH IS THE FRONT-END RULE (owner 2026-09-28): ft3d-combat-distance-v3,
 *  the caster's reach ring touching the target's footprint, including swarm frontage. null for a sizeless caster. */
export function pageDistanceFt(host: Pick<PageTriggerHost, 'value' | 'entries'>, a: Row, b: Row): number | null {
  const has = (row: Row, key: string): boolean => host.value(row, key) != null || host.entries(row, key).length > 0;
  if ([a, b].some(row => has(row, 'swarm_width_lanes') && !getSwarmTemplateForCreature(row as never)))
    throw new TriggerInputUnavailable('swarm-geometry');
  const sizeRead = { value: host.value, has, entries: host.entries, inventoryField: undefined } as unknown as Parameters<typeof combatSizeFt>[1];
  const sizeA = combatSizeFt(a, sizeRead);
  if (sizeA === null) return null;
  const from = combatCenter(a), to = combatCenter(b);
  if (!from || !to) throw new TriggerInputUnavailable('position-not-held');
  const sizeB = combatSizeFt(b, sizeRead) ?? 0;
  return movementReachDistanceFt(swarmMovementBody(a, { diameter: sizeA, height: sizeA }), from,
    swarmMovementBody(b, { diameter: sizeB, height: sizeB }), to);
}

export function pageTriggerReaders(host: PageTriggerHost): TriggerReaders {
  const encounterId = upper(record(host.encounter.encounters)?.encounter_id ?? host.encounter.encounter_id);
  const value = (row: Row, key: string): unknown => host.value(row, key);
  const roster = (): Row[] => (Array.isArray(host.encounter.encounter_creatures_arr) ? host.encounter.encounter_creatures_arr as unknown[] : []).map(record).filter((r): r is Row => !!r);
  return {
    entries: host.entries,
    value: host.value,
    itemField: host.itemField,
    definition: (id, allowLayerlessStatus) => fullDefinition(host.definition(id), allowLayerlessStatus),
    inCombat: row => inActiveCombat(host.encounter, row),
    scalar: (row, field) => {
      const ec = record(row.encounter_creatures);
      const v = scalar(ec?.[field]);
      if (v === undefined || v === null || v === '') {
        if (ec?.encounter_creature_id && ec?.encounter_id && EC_LEAN_ZERO.has(field)) return 0;
        throw new TriggerInputUnavailable(`scalar-not-held:${field}`);
      }
      const n = Number(v);
      if (!Number.isFinite(n)) throw new TriggerInputUnavailable(`scalar-unreadable:${field}`);
      return Math.trunc(n);
    },
    currentCharges: (entry, abilityId, caster) => heldTriggerCharges(entry, abilityId, caster, { ...host, roster }),
    opposing: (a, b) => opposingTeams(a, b, value),
    distanceFt: (a, b) => pageDistanceFt(host, a, b),
    // Self / Unlimited pass; any other authored range with an inciting target hands back (triggerRangePasses).
    inRange: (_caster, _target, ability) => triggerRangePasses(ability, host.value),
    statusItemIds: row => storedStatusItemIds(row),
    vStatusId: row => {
      const v = record(record(row.computed)?.vitality)?.v_status;
      if (v === undefined || v === null || v === '') return '';
      return typeof v === 'string' ? v : String(record(record(v)?.items)?.item_id ?? scalar(v) ?? '');
    },
    naturalWeaponCount: row => naturalWeaponCount(row, (r, k) => host.entries(r, k)),
    pl: definition => frontEndPl(definition, id => host.definition(id), record(host.encounter.catalog)),
    position: posCoord,
    encounterId,
    roster,
  };
}
